/**
 * Typed fetch client for the account-scoped Soroush_API dashboard endpoints — the five read routes;
 * since the Granular_CRUD_API (Feature: granular-crud-api, task 12.1; Requirements 11.1, 11.4,
 * 11.5, 12.1; Design: §11), the eight Check / Alert_Rule read-write routes; and since AI Check
 * generation (Feature: ai-check-generation, task 23.1; Requirements 9.1, 9.3, 9.4, 9.5, 9.8;
 * Design: §Components 11), the nine generation and Draft_Check routes; since user and data
 * management (Feature: user-and-data-management; Requirements 3, 4, 5, 6, 7, 8.1), the six membership
 * and invitation routes plus the four profile routes; and since the Terraform provider (Feature:
 * terraform-provider, task 6.1; Requirement 11.1; Design: §Components 8), the four API-key management
 * routes; and since customer-managed inference (Feature: customer-managed-inference, task 17.1;
 * Requirements 13.2, 13.4; Design: §Components 22), the five Inference_Configuration routes — one
 * method per route, named after the resource each addresses.
 *
 * The four profile methods take **no subject**, and that is a design constraint rather than an omission:
 * Requirement 1.6 forbids taking a user identifier from request input, so the API derives the person from
 * the credential. A `getProfile(sub)` would suggest the route accepted one, and a route that did would be
 * an admin-impersonation primitive.
 *
 * One request in the generation flow is not an API call at all: the presigned Source_Document PUT
 * goes to the Source_Document_Store on another origin, carries no bearer token, and must echo the
 * headers the presign response named. It is {@link uploadSourceDocument}, a standalone function
 * rather than an {@link ApiClient} method, for exactly those reasons.
 *
 * `createApiClient(baseUrl, getAccessToken)` returns a small typed wrapper that attaches
 * `Authorization: Bearer <token>` to every request (the token is read lazily per call, so a
 * refreshed token is always used).
 *
 * It also sends `x-soroush-account` when an Account has been selected (Req 5.8). This does **not**
 * make account scope a client concern: the header only *selects* among the Accounts the credential
 * already permits, and the server verifies a membership row before honouring anything other than the
 * token's own Account. An unrecognised or unpermitted value is refused, never silently downgraded to
 * the home Account. Sending the home Account explicitly is free — the server's home path short-circuits
 * before any store read.
 *
 * Errors surface as {@link ApiError}: a `401` sets `isAuthError` so the UI can prompt re-auth, and
 * any other non-2xx carries the status and a best-effort message parsed from the JSON error body.
 * A `400 { error: 'validation_failed', errors }` additionally carries the per-field
 * {@link ApiError.fieldErrors}, which is what lets a form place each message beside its input
 * (Req 11.5) instead of dumping one opaque string.
 */

import type { FieldError } from '@soroush/core';

import type {
  AcceptCheckDraftResponse,
  AlertRulePatch,
  ChangePasswordPayload,
  ChangePasswordResponse,
  CheckDraftPatch,
  CheckPatch,
  CreateAlertRulePayload,
  CreateAlertRuleResponse,
  CreateApiKeyPayload,
  CreateApiKeyResponse,
  CreateCheckPayload,
  CreateCheckResponse,
  CreateGenerationRequestPayload,
  CreateGenerationRequestResponse,
  CreateInvitationPayload,
  CreateInvitationResponse,
  CreateSourceDocumentUploadUrlPayload,
  CreateSourceDocumentUploadUrlResponse,
  DeleteAlertRuleResponse,
  DeleteCheckResponse,
  DiscardCheckDraftResponse,
  GetAlertRuleResponse,
  GetCheckDraftResponse,
  GetCheckResponse,
  GetGenerationRequestResponse,
  GetInferenceConfigurationResponse,
  GetInferenceRolePolicyResponse,
  GetPersonalDataExportResponse,
  GetProfileResponse,
  GetResultResponse,
  GetTraceResponse,
  ListAccountsResponse,
  ListAlertRulesResponse,
  ListApiKeysResponse,
  ListCheckDraftsResponse,
  ListChecksResponse,
  ListGenerationRequestsResponse,
  ListInvitationsResponse,
  ListMembersResponse,
  ListResultsResponse,
  MemberRolePatch,
  PatchCheckDraftResponse,
  ProfileNamePatch,
  RemoveMemberResponse,
  ReplaceInferenceConfigurationPayload,
  ReplaceInferenceConfigurationResponse,
  ResultRangeQuery,
  RevokeApiKeyResponse,
  RevokeInvitationResponse,
  RotateInferenceExternalIdResponse,
  UpdateAlertRuleResponse,
  UpdateCheckResponse,
  UpdateMemberResponse,
  UpdateProfileResponse,
  VerifyInferenceConfigurationResponse,
} from './types';

/** A getter for the current access token (JWT). Returns `undefined`/`null` when not signed in. */
export type AccessTokenGetter = () => string | null | undefined;

/**
 * An error from an API call: carries the HTTP status and, for `401`, an auth-error flag. For a
 * `validation_failed` response it also carries the per-field errors the API listed, so a form can
 * map them onto its inputs; for every other failure `fieldErrors` is `undefined`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly isAuthError: boolean;
  /** Per-field errors from a `400 validation_failed` body; absent on any other failure. */
  readonly fieldErrors?: readonly FieldError[];

  constructor(status: number, message: string, fieldErrors?: readonly FieldError[]) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.isAuthError = status === 401;
    if (fieldErrors !== undefined) this.fieldErrors = fieldErrors;
  }
}

/** The read and write operations the dashboard performs against the Soroush_API. */
export interface ApiClient {
  listChecks(): Promise<ListChecksResponse>;
  getCheck(checkId: string): Promise<GetCheckResponse>;
  listCheckResults(checkId: string, range?: ResultRangeQuery): Promise<ListResultsResponse>;
  getResult(resultId: string): Promise<GetResultResponse>;
  getTrace(traceId: string): Promise<GetTraceResponse>;
  createCheck(payload: CreateCheckPayload): Promise<CreateCheckResponse>;
  updateCheck(checkId: string, patch: CheckPatch): Promise<UpdateCheckResponse>;
  deleteCheck(checkId: string): Promise<DeleteCheckResponse>;
  listAlertRules(): Promise<ListAlertRulesResponse>;
  getAlertRule(ruleId: string): Promise<GetAlertRuleResponse>;
  createAlertRule(payload: CreateAlertRulePayload): Promise<CreateAlertRuleResponse>;
  updateAlertRule(ruleId: string, patch: AlertRulePatch): Promise<UpdateAlertRuleResponse>;
  deleteAlertRule(ruleId: string): Promise<DeleteAlertRuleResponse>;

  /* -- The generation plane (ai-check-generation Req 9.1, 9.3, 9.4, 9.5, 9.8) -- */

  /**
   * `POST /v1/source-documents/upload-url` — presign one Source_Document upload (Req 9.1).
   *
   * Returns the platform-minted `documentKey`, the URL to PUT to, and the exact
   * `requiredHeaders` that PUT must carry. Both must be **echoed, not recomputed**: pass the
   * whole response to {@link uploadSourceDocument}, then the same `documentKey` to
   * {@link ApiClient.createGenerationRequest}.
   */
  createSourceDocumentUploadUrl(
    payload: CreateSourceDocumentUploadUrlPayload,
  ): Promise<CreateSourceDocumentUploadUrlResponse>;

  /**
   * `POST /v1/generation-requests` — submit exactly one Source_Document (Req 9.1). Answers `202`
   * with the request id to poll; generation itself is asynchronous.
   */
  createGenerationRequest(
    payload: CreateGenerationRequestPayload,
  ): Promise<CreateGenerationRequestResponse>;

  /**
   * `GET /v1/generation-requests/{id}` — one request's progress and, once it reaches a
   * draft-bearing terminal state, the Draft_Check it produced (Req 9.2, 9.6).
   */
  getGenerationRequest(generationRequestId: string): Promise<GetGenerationRequestResponse>;

  /** `GET /v1/generation-requests` — the Account's requests, in chronological order. */
  listGenerationRequests(): Promise<ListGenerationRequestsResponse>;

  /** `GET /v1/check-drafts` — the Account's drafts in every state, with expiry (Req 9.8). */
  listCheckDrafts(): Promise<ListCheckDraftsResponse>;

  /** `GET /v1/check-drafts/{id}` — one draft's configuration and outstanding errors (Req 9.3). */
  getCheckDraft(draftId: string): Promise<GetCheckDraftResponse>;

  /**
   * `PATCH /v1/check-drafts/{id}` — apply a partial Check configuration to a stored draft
   * (Req 9.5). An invalid edit is a `400 validation_failed` whose {@link ApiError.fieldErrors} the
   * review form places beside the corresponding inputs; the stored draft is left unchanged.
   */
  patchCheckDraft(draftId: string, patch: CheckDraftPatch): Promise<PatchCheckDraftResponse>;

  /**
   * `POST /v1/check-drafts/{id}/accept` — the **only** route that creates a Check from a draft
   * (Req 9.4). Sends no body. `201` on first acceptance, `200` on a replay.
   */
  acceptCheckDraft(draftId: string): Promise<AcceptCheckDraftResponse>;

  /**
   * `DELETE /v1/check-drafts/{id}` — mark a draft discarded, creating no Check (Req 9.4). The
   * draft stays readable and listable until it expires.
   */
  discardCheckDraft(draftId: string): Promise<DiscardCheckDraftResponse>;

  /* -- The membership plane (user-and-data-management Req 5, 6, 7) -- */

  /**
   * `GET /v1/accounts` — every Account the signed-in User can act as, and their role in each
   * (Req 5.8). Backs the Account switcher.
   *
   * The only method here whose answer is **not** scoped to the selected Account: it asks which
   * Accounts the credential could select. Safe to call before any Account is selected, which is what
   * lets it bootstrap the switcher.
   */
  listAccounts(): Promise<ListAccountsResponse>;

  /**
   * `GET /v1/members` — the Account's members with their roles (Req 5.1). A **read**: knowing who
   * can see the Account's data is not a privileged question, which is Requirement 6's premise.
   */
  listMembers(): Promise<ListMembersResponse>;

  /**
   * `POST /v1/members/invitations` — record a pending invitation (Req 5.2). Grants nothing until the
   * invitee accepts. The disclosure Requirement 6 mandates is a precondition of *calling* this, and
   * lives in {@link import('../components/SharingDisclosure').SharingDisclosure}.
   */
  createInvitation(payload: CreateInvitationPayload): Promise<CreateInvitationResponse>;

  /** `GET /v1/members/invitations` — the Account's invitations in every state (Req 5.2, 5.5). */
  listInvitations(): Promise<ListInvitationsResponse>;

  /** `DELETE /v1/members/invitations/{id}` — withdraw an invitation before it is accepted. */
  revokeInvitation(inviteId: string): Promise<RevokeInvitationResponse>;

  /**
   * `PATCH /v1/members/{userSub}` — change one member's role. The last-admin refusal is a
   * `409 conflict` whose message the UI surfaces verbatim rather than paraphrasing (Req 5.7).
   */
  updateMember(userSub: string, patch: MemberRolePatch): Promise<UpdateMemberResponse>;

  /**
   * `DELETE /v1/members/{userSub}` — end a membership (Req 7.1). Also `409` for the last admin, whose
   * message the UI surfaces verbatim. The member's Checks stay with the Account (Req 7.5).
   *
   * **What happens to their API_Keys is partial, and the response says which part.** An earlier version
   * of this comment claimed their keys "are revoked with the membership (Req 7.3)" flatly; that was
   * true of nothing when written and is still an overstatement now. What the platform actually does:
   * keys it can **attribute** to that member — the ones recorded as minted by them — are revoked, and a
   * key recording no minter is **not** revoked and is named back instead (Req 7.7). So the response
   * carries `revokedKeyIds` and `unattributedKeyIds` always, and `failedKeyIds` on a `207` when a
   * revocation that should have succeeded did not (Req 7.10) — see {@link RemoveMemberResponse}.
   *
   * `207` is in the 2xx class, so this method **resolves** with that body rather than throwing: a
   * partial success has to reach the page, or the page cannot tell the truth about it. The confirmation
   * dialogue states the rule before this is called; the post-removal notice reports these ids after.
   */
  removeMember(userSub: string): Promise<RemoveMemberResponse>;

  /* -- The profile plane (user-and-data-management Req 3, 4, 8.1) -- */

  /**
   * `GET /v1/me` — the signed-in User's own profile (Req 3.2).
   *
   * **No parameter, and that is the design rather than a convenience.** Requirement 1.6 forbids taking a
   * user identifier from request input, so the API derives the subject from the credential; a method
   * taking a `sub` would suggest the route accepted one, and a route that did would be an
   * admin-impersonation primitive — the identifiers are on display in the members table.
   *
   * `name` and `email` come back as explicit `null` when absent, never omitted, so the page can state the
   * absence instead of rendering a blank (Req 3.2).
   */
  getProfile(): Promise<GetProfileResponse>;

  /**
   * `PATCH /v1/me` — change the signed-in User's display name (Req 3.3).
   *
   * The patch type is exactly `{ name }`: the API's allow-list refuses `custom:role`,
   * `custom:accountId`, `email_verified`, and `email` with a per-field `validation_failed` (Req 3.6, 3.7),
   * and the narrow type means the SPA has no way to send one of them by accident. Returns the whole
   * profile, so the page replaces its state from one shape whichever route produced it.
   */
  updateProfile(patch: ProfileNamePatch): Promise<UpdateProfileResponse>;

  /**
   * `POST /v1/me/password` — change the signed-in User's password (Req 4.1–4.4).
   *
   * Both failures arrive as a `400 validation_failed` whose {@link ApiError.fieldErrors} the form places
   * beside the right input, and the distinction is the `path`:
   *
   *  - `currentPassword` — the current password was wrong. The response carries **nothing** about
   *    `newPassword`, because Requirement 4.2 forbids revealing whether it would have been acceptable.
   *  - `newPassword` — the new password violated the pool policy, with the failed requirement named
   *    rather than a raw provider error (Req 4.3).
   *
   * On success the session stays valid (Req 4.4) and the User's API keys keep working (Req 4.6); both are
   * stated in the response and both are shown.
   */
  changePassword(payload: ChangePasswordPayload): Promise<ChangePasswordResponse>;

  /**
   * `GET /v1/me/export` — the Article 15 subject-access export (Req 8.1).
   *
   * The caller's own identity and memberships, and nothing belonging to anyone else. A **read**, not a
   * write: reading your own data is not gated on a write role, which is why the page does not put the
   * download behind `useCanWrite`.
   */
  exportPersonalData(): Promise<GetPersonalDataExportResponse>;

  /* -- The API-key plane (terraform-provider Req 11.1) -- */

  /**
   * `GET /v1/api-keys` — the Account's keys in issuance order, oldest first, in every state
   * including revoked and expired (Req 11.6).
   *
   * **Admin-only server-side**, and that is not a detail the page may discover by calling: all four
   * key routes are gated on admin actions, so a viewer or editor gets a `403`. The page therefore
   * gates on the role *in the selected Account* and issues no `GET` at all for a non-admin
   * (Req 11.2) — an error message where an explanation belongs is a worse answer than no request.
   */
  listApiKeys(): Promise<ListApiKeysResponse>;

  /**
   * `POST /v1/api-keys` — mint one key inside the credential's own Account, answering `201` with the
   * API_Key_Value **exactly once** (Req 11.4, 11.5).
   *
   * A rejected `name`, `role`, or `expiresInDays` arrives as a `400 validation_failed` whose
   * {@link ApiError.fieldErrors} the create form places beside the corresponding input, with nothing
   * persisted. The payload carries no `accountId`: the Account is the credential's, and a body naming
   * another is refused with a `403`.
   */
  createApiKey(payload: CreateApiKeyPayload): Promise<CreateApiKeyResponse>;

  /**
   * `POST /v1/api-keys/{id}/rotate` — re-key in place, answering `200` with the replacement
   * API_Key_Value exactly once (Req 11.7). Sends no body; the route takes none.
   *
   * The `keyId` is unchanged, so the listing and whatever is configured against the key stay stable.
   * Two failures the confirmation dialog must surface rather than paraphrase: a `409` on a **revoked**
   * key (rotation writes `state: 'active'`, so rotating a revoked key would defeat revocation
   * finality — the API refuses it with its own message), and a `404` for an id outside the Account.
   */
  rotateApiKey(keyId: string): Promise<CreateApiKeyResponse>;

  /**
   * `DELETE /v1/api-keys/{id}` — revoke a key, permanently (Req 11.7).
   *
   * Idempotent: revoking an already-revoked key answers the same `200`, so a retry after a lost
   * response is not punished. The record is *not* deleted — it stays listed and auditable — which is
   * why the confirmation says "revocation is final" rather than "the key will be removed".
   */
  revokeApiKey(keyId: string): Promise<RevokeApiKeyResponse>;

  /* -- The inference plane (customer-managed-inference Req 13.2, 13.4) -- */

  /**
   * `GET /v1/inference-configuration` — the Account's Inference_Configuration, its
   * Verification_State, its unmasked External_Id, and the Requirement 10.2 spend guards.
   *
   * **Admin-only server-side**, like the four key routes: all of `inference-config:*` is granted to
   * admin alone (Requirements 14.2, 14.3), so a viewer or editor gets a `403`. The page therefore
   * gates on the role *in the selected Account* and issues no `GET` at all for a non-admin
   * (Requirement 13.5).
   *
   * **This read writes once**, on an Account with no stored record: the server mints and persists an
   * External_Id under a conditional write, because the value must exist before the customer's trust
   * policy can condition on it. It is idempotent — a second call returns the same value and writes
   * nothing — so nothing on the client side has to know.
   */
  getInferenceConfiguration(): Promise<GetInferenceConfigurationResponse>;

  /**
   * `PUT /v1/inference-configuration` — replace the configuration (Requirements 9.1, 9.2, 13.3).
   *
   * A whole-record replace, not a patch: an omitted Model_Pin is a **removed** Model_Pin. Two
   * failures the page must distinguish and neither of which changes anything:
   *
   *  - `400 validation_failed`, whose {@link ApiError.fieldErrors} carry the server's own paths
   *    rooted at `inferenceConfiguration.` — placed beside the corresponding inputs, with nothing
   *    persisted and no role assumed.
   *  - `409 conflict`, when a `customer_hosted` target could not be verified. The Inference_Mode
   *    **stays where it was**, so a refused configuration does not take a working Account's
   *    generation offline; the body names the fault and the page renders it.
   */
  putInferenceConfiguration(
    payload: ReplaceInferenceConfigurationPayload,
  ): Promise<ReplaceInferenceConfigurationResponse>;

  /**
   * `POST /v1/inference-configuration/rotate-external-id` — issue a replacement External_Id and set
   * the Verification_State to `unverified` (Requirement 2.8). Sends no body; the route takes none.
   *
   * Not a mode change: an Account in `customer_hosted` stays there, and its generations then fail
   * loudly with `not_verified` rather than running under a trust-policy condition that no longer
   * matches. The response's `advisory` says what must happen before inference resumes, and the page
   * renders it verbatim rather than paraphrasing it.
   */
  rotateInferenceExternalId(): Promise<RotateInferenceExternalIdResponse>;

  /**
   * `POST /v1/inference-configuration/verify` — re-run role assumption and every distinct
   * Inference_Profile's capability probe against the **stored** configuration (Requirement 4.9).
   * Sends no body.
   *
   * The route an administrator uses after fixing something in their own account. It changes the
   * Verification_State, the probe outcomes, and the recorded failure, and **never the
   * Inference_Mode**. A `409` when there is no customer configuration to act on, and a `409`
   * carrying the failure when a probe did not pass — a throttled probe leaves the state `unverified`
   * rather than `failed`, which is "we could not tell yet" and not "your configuration is wrong".
   */
  verifyInferenceConfiguration(): Promise<VerifyInferenceConfigurationResponse>;

  /**
   * `GET /v1/inference-configuration/role-policy` — the role to create in the customer's own account,
   * its trust and permission policies, and the Terraform module that applies both (Requirements 3.1,
   * 3.6, 3.7).
   *
   * A `409` before any customer configuration is stored, because the permission policy's `Resource`
   * list is derived from the region and the selections. That is not a blocker for setup: the
   * External_Id and the Inference_Principal ARN — together the whole trust policy — are on
   * {@link ApiClient.getInferenceConfiguration}.
   */
  getInferenceRolePolicy(): Promise<GetInferenceRolePolicyResponse>;
}

/**
 * The HTTP methods the dashboard issues — the read `GET` plus the write verbs (Req 12.1).
 *
 * `PUT` joined the set for the Inference_Configuration replace
 * (customer-managed-inference Requirement 13.2), which is a whole-record replace rather than a merge
 * patch and is spelled as the verb that means that.
 */
type HttpVerb = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Join a base URL and a path without doubling or dropping the separating slash. */
function joinUrl(baseUrl: string, path: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/** Extract a human-readable message from a parsed JSON error body, falling back to the status text. */
function messageFromBody(body: unknown, fallback: string): string {
  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>;
    if (typeof record.message === 'string' && record.message.length > 0) return record.message;
    // A validation_failed body carries no `message` — its detail is the per-field error list — so
    // give the UI a readable summary rather than the raw error code.
    if (record.error === VALIDATION_FAILED) return 'Validation failed';
    if (typeof record.error === 'string' && record.error.length > 0) return record.error;
  }
  return fallback;
}

/** The API's `error` discriminant for a per-field validation rejection (`400`). */
const VALIDATION_FAILED = 'validation_failed';

function isFieldError(value: unknown): value is FieldError {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.path === 'string' && typeof record.message === 'string';
}

/**
 * The per-field errors of a `validation_failed` body, or `undefined` for any other failure — so
 * `fieldErrors === undefined` cleanly distinguishes "the API rejected specific fields" from
 * "something else went wrong" (a `401`, a `404`, a `500`, a network error).
 */
function fieldErrorsFromBody(body: unknown): FieldError[] | undefined {
  if (typeof body !== 'object' || body === null) return undefined;
  const record = body as Record<string, unknown>;
  if (record.error !== VALIDATION_FAILED) return undefined;
  return Array.isArray(record.errors) ? record.errors.filter(isFieldError) : [];
}

/**
 * The header naming which Account a request acts as (Req 5.8).
 *
 * Must match `REQUESTED_ACCOUNT_HEADER` in `@soroush/services` `identity/authorizer-lambda.ts`. The
 * literal is duplicated rather than imported because the SPA does not depend on the services bundle,
 * for the same reason the IaC declares its route paths literally. A mismatch degrades safely — the
 * server treats an unrecognised header as absent and serves the home Account — which is precisely why
 * it needs asserting rather than trusting: the switcher would silently stop switching.
 */
export const REQUESTED_ACCOUNT_HEADER = 'x-soroush-account';

/**
 * The single Inference_Configuration resource path — there is exactly one per Account
 * (Requirement 1.5), and the Account comes from the credential, so none of the five routes takes a
 * path parameter and there is no `inferenceConfigurationPath(id)` helper to write.
 *
 * A constant rather than four literals, because the three sub-routes are built by suffixing it and a
 * drift between them would be a `404` on one route only.
 */
const INFERENCE_CONFIGURATION_PATH = '/v1/inference-configuration';

/**
 * Supplies the Account the caller wants to act as, or `undefined` for the credential's own.
 *
 * Read lazily per request, like the token, so a switch does not require rebuilding the client — though
 * `useApiClient` rebuilds anyway, deliberately, because that is what makes every page refetch.
 */
export type RequestedAccountGetter = () => string | undefined;

export function createApiClient(
  baseUrl: string,
  getAccessToken: AccessTokenGetter,
  fetchImpl: typeof fetch = fetch,
  getRequestedAccount: RequestedAccountGetter = () => undefined,
): ApiClient {
  /**
   * Issue one request. `method` defaults to `GET`; when a `body` is present it is JSON-encoded and
   * the `content-type: application/json` header is set (the write routes parse their payload as
   * JSON), so a caller never hand-builds headers.
   */
  async function request<T>(
    path: string,
    options: { method?: HttpVerb; body?: unknown } = {},
  ): Promise<T> {
    const method = options.method ?? 'GET';
    const token = getAccessToken();
    const headers: Record<string, string> = { accept: 'application/json' };
    if (token !== null && token !== undefined && token.length > 0) {
      headers.authorization = `Bearer ${token}`;
    }
    const hasBody = options.body !== undefined;
    if (hasBody) headers['content-type'] = 'application/json';
    // Omitted entirely when no Account is selected, rather than sent empty: the server reads a blank
    // value as absent, so an empty header would work by accident and hide a bug in the selection.
    const requestedAccount = getRequestedAccount();
    if (requestedAccount !== undefined && requestedAccount.length > 0) {
      headers[REQUESTED_ACCOUNT_HEADER] = requestedAccount;
    }

    let response: Response;
    try {
      response = await fetchImpl(joinUrl(baseUrl, path), {
        method,
        headers,
        ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      });
    } catch (cause) {
      throw new ApiError(0, `Network error calling ${path}: ${(cause as Error).message}`);
    }

    if (!response.ok) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        body = undefined;
      }
      throw new ApiError(
        response.status,
        messageFromBody(body, `Request to ${path} failed with HTTP ${response.status}`),
        fieldErrorsFromBody(body),
      );
    }

    return (await response.json()) as T;
  }

  function checkPath(checkId: string): string {
    return `/v1/checks/${encodeURIComponent(checkId)}`;
  }

  function alertRulePath(ruleId: string): string {
    return `/v1/alert-rules/${encodeURIComponent(ruleId)}`;
  }

  function checkDraftPath(draftId: string): string {
    return `/v1/check-drafts/${encodeURIComponent(draftId)}`;
  }

  function memberPath(userSub: string): string {
    return `/v1/members/${encodeURIComponent(userSub)}`;
  }

  function invitationPath(inviteId: string): string {
    return `/v1/members/invitations/${encodeURIComponent(inviteId)}`;
  }

  function apiKeyPath(keyId: string): string {
    return `/v1/api-keys/${encodeURIComponent(keyId)}`;
  }

  function buildResultsPath(checkId: string, range?: ResultRangeQuery): string {
    const params = new URLSearchParams();
    if (range?.from !== undefined && range.from.length > 0) params.set('from', range.from);
    if (range?.to !== undefined && range.to.length > 0) params.set('to', range.to);
    const query = params.toString();
    const base = `/v1/checks/${encodeURIComponent(checkId)}/results`;
    return query.length > 0 ? `${base}?${query}` : base;
  }

  return {
    listChecks: () => request<ListChecksResponse>('/v1/checks'),
    getCheck: (checkId) => request<GetCheckResponse>(checkPath(checkId)),
    listCheckResults: (checkId, range) => request<ListResultsResponse>(buildResultsPath(checkId, range)),
    getResult: (resultId) => request<GetResultResponse>(`/v1/results/${encodeURIComponent(resultId)}`),
    getTrace: (traceId) => request<GetTraceResponse>(`/v1/traces/${encodeURIComponent(traceId)}`),

    // The Granular_CRUD_API surface (Req 12.1): six write routes plus the two Alert_Rule reads.
    createCheck: (payload) =>
      request<CreateCheckResponse>('/v1/checks', { method: 'POST', body: payload }),
    updateCheck: (checkId, patch) =>
      request<UpdateCheckResponse>(checkPath(checkId), { method: 'PATCH', body: patch }),
    deleteCheck: (checkId) => request<DeleteCheckResponse>(checkPath(checkId), { method: 'DELETE' }),
    listAlertRules: () => request<ListAlertRulesResponse>('/v1/alert-rules'),
    getAlertRule: (ruleId) => request<GetAlertRuleResponse>(alertRulePath(ruleId)),
    createAlertRule: (payload) =>
      request<CreateAlertRuleResponse>('/v1/alert-rules', { method: 'POST', body: payload }),
    updateAlertRule: (ruleId, patch) =>
      request<UpdateAlertRuleResponse>(alertRulePath(ruleId), { method: 'PATCH', body: patch }),
    deleteAlertRule: (ruleId) =>
      request<DeleteAlertRuleResponse>(alertRulePath(ruleId), { method: 'DELETE' }),

    // The generation plane (ai-check-generation Req 9.1, 9.3, 9.4, 9.5, 9.8): nine routes, one
    // method each. Every failure arrives as an ApiError through the same `request`, so a draft's
    // `400 validation_failed` already carries `fieldErrors` with no generation-specific parsing.
    createSourceDocumentUploadUrl: (payload) =>
      request<CreateSourceDocumentUploadUrlResponse>('/v1/source-documents/upload-url', {
        method: 'POST',
        body: payload,
      }),
    createGenerationRequest: (payload) =>
      request<CreateGenerationRequestResponse>('/v1/generation-requests', {
        method: 'POST',
        body: payload,
      }),
    getGenerationRequest: (generationRequestId) =>
      request<GetGenerationRequestResponse>(
        `/v1/generation-requests/${encodeURIComponent(generationRequestId)}`,
      ),
    listGenerationRequests: () =>
      request<ListGenerationRequestsResponse>('/v1/generation-requests'),
    listCheckDrafts: () => request<ListCheckDraftsResponse>('/v1/check-drafts'),
    getCheckDraft: (draftId) => request<GetCheckDraftResponse>(checkDraftPath(draftId)),
    patchCheckDraft: (draftId, patch) =>
      request<PatchCheckDraftResponse>(checkDraftPath(draftId), { method: 'PATCH', body: patch }),
    // No body: the route takes none, so no `content-type` is set either.
    acceptCheckDraft: (draftId) =>
      request<AcceptCheckDraftResponse>(`${checkDraftPath(draftId)}/accept`, { method: 'POST' }),
    discardCheckDraft: (draftId) =>
      request<DiscardCheckDraftResponse>(checkDraftPath(draftId), { method: 'DELETE' }),

    // The membership plane (user-and-data-management Req 5, 6, 7): six routes, one method each.
    // `/v1/members/invitations` is a sub-collection, so the invitation paths are built from their own
    // helper rather than from `memberPath` — an invitation is keyed by its own id, not by a userSub,
    // and routing it through `memberPath` would encode a member path for an invitation resource.
    listAccounts: () => request<ListAccountsResponse>('/v1/accounts'),
    listMembers: () => request<ListMembersResponse>('/v1/members'),
    createInvitation: (payload) =>
      request<CreateInvitationResponse>('/v1/members/invitations', {
        method: 'POST',
        body: payload,
      }),
    listInvitations: () => request<ListInvitationsResponse>('/v1/members/invitations'),
    revokeInvitation: (inviteId) =>
      request<RevokeInvitationResponse>(invitationPath(inviteId), { method: 'DELETE' }),
    updateMember: (userSub, patch) =>
      request<UpdateMemberResponse>(memberPath(userSub), { method: 'PATCH', body: patch }),
    removeMember: (userSub) =>
      request<RemoveMemberResponse>(memberPath(userSub), { method: 'DELETE' }),

    // The profile plane (user-and-data-management Req 3, 4, 8.1): four routes, one method each, and NONE
    // of them takes an identifier — the subject is the credential's own `sub` (Req 1.6). There is
    // deliberately no `deleteProfile`: Requirement 11's identity deletion is not built (its preconditions
    // are not), and a client method for a route that does not exist would read as an oversight in the API.
    getProfile: () => request<GetProfileResponse>('/v1/me'),
    updateProfile: (patch) =>
      request<UpdateProfileResponse>('/v1/me', { method: 'PATCH', body: patch }),
    changePassword: (payload) =>
      request<ChangePasswordResponse>('/v1/me/password', { method: 'POST', body: payload }),
    exportPersonalData: () => request<GetPersonalDataExportResponse>('/v1/me/export'),

    // The API-key plane (terraform-provider Req 11.1): four routes that already exist server-side,
    // reached through the same `request` as every other method — same `authorization`, same
    // `x-soroush-account`, same `fieldErrors` parsing, and **no new request header**. That last part is
    // load-bearing rather than tidy: the API's CORS `allowHeaders` set has exactly three members, a
    // fourth would need a `cdk deploy`, and Milestone 1 claims it needs none (Design decision 8). The
    // three-member assertion in `iac/.../soroush-api-stack.test.ts` is the tripwire; the assertion in
    // `client.test.ts` that no key request carries a name outside that set is its client-side half.
    //
    // The provider declares its provenance over `X-Soroush-Origin`, and the dashboard deliberately
    // never sends it: a dashboard write *is* an API write (Req 5.3), so the absent header is what makes
    // the SPA stamp `origin: 'api'` without asking.
    listApiKeys: () => request<ListApiKeysResponse>('/v1/api-keys'),
    createApiKey: (payload) =>
      request<CreateApiKeyResponse>('/v1/api-keys', { method: 'POST', body: payload }),
    // No body: the rotate route takes none, so no `content-type` is set either — the same shape as
    // `acceptCheckDraft`. A rotate response is a create response; one type serves both.
    rotateApiKey: (keyId) =>
      request<CreateApiKeyResponse>(`${apiKeyPath(keyId)}/rotate`, { method: 'POST' }),
    revokeApiKey: (keyId) => request<RevokeApiKeyResponse>(apiKeyPath(keyId), { method: 'DELETE' }),

    // The inference plane (customer-managed-inference Req 13.2, 13.4): five routes, one method each,
    // through the same `request` as everything else — same `authorization`, same `x-soroush-account`,
    // same `fieldErrors` parsing, and **no new request header**. The API's CORS `allowHeaders` set has
    // exactly three members, a fourth would need a `cdk deploy`, and this feature's Milestone claims it
    // needs none for the SPA: the three-member assertion in `iac/.../soroush-api-stack.test.ts` is the
    // tripwire and the header assertion in `client.test.ts` is its client-side half.
    //
    // The two `POST`s send no body, so no `content-type` is set for them either — the same shape as
    // `rotateApiKey` and `acceptCheckDraft`. `PUT` is the replace, and it carries the entity nested
    // under `inferenceConfiguration`, which is also the root of every returned `FieldError` path.
    getInferenceConfiguration: () =>
      request<GetInferenceConfigurationResponse>(INFERENCE_CONFIGURATION_PATH),
    putInferenceConfiguration: (payload) =>
      request<ReplaceInferenceConfigurationResponse>(INFERENCE_CONFIGURATION_PATH, {
        method: 'PUT',
        body: payload,
      }),
    rotateInferenceExternalId: () =>
      request<RotateInferenceExternalIdResponse>(
        `${INFERENCE_CONFIGURATION_PATH}/rotate-external-id`,
        { method: 'POST' },
      ),
    verifyInferenceConfiguration: () =>
      request<VerifyInferenceConfigurationResponse>(`${INFERENCE_CONFIGURATION_PATH}/verify`, {
        method: 'POST',
      }),
    getInferenceRolePolicy: () =>
      request<GetInferenceRolePolicyResponse>(`${INFERENCE_CONFIGURATION_PATH}/role-policy`),
  };
}

/**
 * PUT a Source_Document's bytes to the URL {@link ApiClient.createSourceDocumentUploadUrl} presigned
 * (Req 9.1).
 *
 * A standalone function rather than an {@link ApiClient} method, because this is the one request in
 * the flow that does **not** go to the Soroush_API:
 *
 *  - **No bearer token.** The target is the Source_Document_Store, a different origin; the
 *    signature is already in the URL, so an `Authorization` header would leak the credential to
 *    another host and S3 would reject the request anyway. The absent parameter is the enforcement —
 *    this function has no access to the token getter.
 *  - **The headers are `presigned.requiredHeaders`, verbatim.** `content-type` and `content-length`
 *    are *signed* values that S3 compares byte for byte, so recomputing either earns an opaque
 *    `403` that says nothing about which one was wrong.
 *  - **A failure body is XML, not JSON.** So the error is raised directly rather than through the
 *    API client's JSON error parsing, and never carries `fieldErrors` — S3 has no field to name.
 *
 * `body` is what a file input yields: a `File` (which is a `Blob`) or its `ArrayBuffer`.
 */
export async function uploadSourceDocument(
  presigned: CreateSourceDocumentUploadUrlResponse,
  body: Blob | ArrayBuffer,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  let response: Response;
  try {
    response = await fetchImpl(presigned.uploadUrl, {
      method: 'PUT',
      headers: { ...presigned.requiredHeaders },
      body,
    });
  } catch (cause) {
    throw new ApiError(
      0,
      `Network error uploading the source document: ${(cause as Error).message}`,
    );
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      `Uploading the source document failed with HTTP ${response.status}`,
    );
  }
}
